import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

class CustomPrice extends HTMLElement {
  constructor() {
    super();

    this.scope = null;

    this.tiers = [];
    this.tierElements = [];

    this.priceElement = null;
    this.basePriceHTML = '';

    this.handleQuantityUpdate = this.handleQuantityUpdate.bind(this);
    this.handleProductSelect = this.handleProductSelect.bind(this);
  }

  connectedCallback() {
    this.scope =
      this.closest('.product-details') ??
      this.closest('.shopify-section, dialog');

    if (!this.scope) {
      console.warn('[custom-price] Product scope not found.');
      return;
    }

    this.readTiers();

    this.scope.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.handleQuantityUpdate
    );

    this.scope.addEventListener(
      StandardEvents.productSelect,
      this.handleProductSelect
    );

    this.captureBasePrice();

    this.updatePrice(this.getQuantity());
  }

  disconnectedCallback() {
    if (!this.scope) return;

    this.scope.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.handleQuantityUpdate
    );

    this.scope.removeEventListener(
      StandardEvents.productSelect,
      this.handleProductSelect
    );
  }

  readTiers() {
    this.tierElements = [
      ...this.querySelectorAll('[data-price-tier]'),
    ];

    this.tiers = this.tierElements
      .map((element) => ({
        element,
        minQuantity: Number(element.dataset.minQuantity),
        unitPrice: Number(element.dataset.unitPrice),
        formattedPrice: element.dataset.formattedPrice,
      }))
      .filter(
        (tier) =>
          Number.isFinite(tier.minQuantity) &&
          Number.isFinite(tier.unitPrice)
      )
      .sort((a, b) => a.minQuantity - b.minQuantity);
  }

  getQuantitySelector() {
    return this.scope?.querySelector(
      'quantity-selector-component'
    );
  }

  getQuantity() {
    const quantitySelector = this.getQuantitySelector();

    if (!quantitySelector) {
      return 1;
    }

    if (typeof quantitySelector.getValue === 'function') {
      const quantity = Number(quantitySelector.getValue());

      return Number.isFinite(quantity) ? quantity : 1;
    }

    const input = quantitySelector.querySelector(
      '[ref="quantityInput"]'
    );

    const quantity = Number(input?.value);

    return Number.isFinite(quantity) ? quantity : 1;
  }

  getProductPrice() {
    if (!this.scope) return null;

    const productId = this.dataset.productId;

    if (productId) {
      const productPrice = this.scope.querySelector(
        `product-price[data-product-id="${CSS.escape(productId)}"]`
      );

      if (productPrice) {
        return productPrice;
      }
    }

    return this.scope.querySelector('product-price');
  }

  getLivePriceElement() {
    const productPrice = this.getProductPrice();

    return productPrice?.querySelector(
      '[ref="priceContainer"] .price'
    ) ?? null;
  }

  captureBasePrice() {
    const priceElement = this.getLivePriceElement();

    if (!priceElement) {
      console.warn('[custom-price] Product price not found.');
      return;
    }

    this.priceElement = priceElement;
    this.basePriceHTML = priceElement.innerHTML;
  }

  getActiveTier(quantity) {
    let activeTier = null;

    for (const tier of this.tiers) {
      if (quantity >= tier.minQuantity) {
        activeTier = tier;
      } else {
        break;
      }
    }

    return activeTier;
  }

  updatePrice(quantity) {
    const livePriceElement = this.getLivePriceElement();

    if (!livePriceElement) return;

    /*
     * Shopify może wymienić cały priceContainer,
     * np. po zmianie wariantu.
     */
    if (livePriceElement !== this.priceElement) {
      this.priceElement = livePriceElement;
      this.basePriceHTML = livePriceElement.innerHTML;
    }

    const activeTier = this.getActiveTier(quantity);

    this.updateActiveTier(activeTier);

    /*
     * Brak aktywnego progu:
     * przywróć normalną cenę Shopify.
     */
    if (!activeTier) {
      this.priceElement.innerHTML = this.basePriceHTML;
      return;
    }

    this.setDisplayedPrice(activeTier.formattedPrice);
  }

  setDisplayedPrice(formattedPrice) {
    if (!this.priceElement) return;

    /*
     * Horizon czasem umieszcza w .price również
     * visually-hidden opis dla screen readera.
     *
     * Nie chcemy go usuwać.
     */
    const accessibleLabel =
      this.priceElement.querySelector('.visually-hidden');

    if (accessibleLabel) {
      const label = accessibleLabel.cloneNode(true);

      this.priceElement.replaceChildren(
        label,
        document.createTextNode(formattedPrice)
      );

      return;
    }

    this.priceElement.textContent = formattedPrice;
  }

  updateActiveTier(activeTier) {
    for (const tier of this.tiers) {
      tier.element.toggleAttribute(
        'data-active',
        tier === activeTier
      );
    }
  }

  handleQuantityUpdate(event) {
    const quantity = Number(event.detail?.quantity);

    if (!Number.isFinite(quantity)) return;

    this.updatePrice(quantity);
  }

  handleProductSelect(event) {
    /*
     * Horizon sam pobiera HTML nowego wariantu
     * i aktualizuje <product-price>.
     *
     * Czekamy aż skończy, a dopiero potem ponownie
     * nakładamy naszą cenę progową.
     */
    if (!event.promise) return;

    event.promise
      .then(() => {
        requestAnimationFrame(() => {
          this.priceElement = null;
          this.basePriceHTML = '';

          this.captureBasePrice();

          this.updatePrice(this.getQuantity());
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[custom-price] Product update failed:',
            error
          );
        }
      });
  }
}

if (!customElements.get('custom-price')) {
  customElements.define('custom-price', CustomPrice);
}